import { PageSkeleton, FormPageSkeleton, DashboardSkeleton, MarketAnalysisSkeleton, CalculatorsSkeleton, BRRRRSkeleton, RenovationSkeleton } from "@/components/PageSkeleton";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { BottomNav } from "@/components/layout/BottomNav";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useEffect, useState, lazy, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PageTransition } from "@/components/layout/PageTransition";
import { LazyRoute } from "@/components/layout/LazyRoute";
import { useAfterPaint } from "@/lib/useAfterPaint";
import { useStagedMount } from "@/lib/useStagedMount";
import { prefetchHeavyRoutesOnIdle } from "@/lib/routePrefetch";
import { deferAnalytics } from "@/lib/analytics";
import { RouteMeta } from "@/components/RouteMeta";
import { BrandMark } from "@/components/BrandMark";

// Defer the Radix-based Toaster (~5 KB) — toast notifications can't fire
// before user interaction, so push it out of the entry chunk.
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
// Defer ThemeToggle — interactive control, not LCP-critical.
const ThemeToggle = lazy(() => import("@/components/ThemeToggle").then(m => ({ default: m.ThemeToggle })));


// Desktop-only sidebar — hidden on mobile, so defer it from the entry chunk.
const AppSidebar = lazy(() => import("@/components/layout/AppSidebar").then(m => ({ default: m.AppSidebar })));
// Lazy-load the landing page so non-home routes don't pull its framer-motion
// usage and dashboard widget bundle into the entry chunk.
const Index = lazy(() => import("./pages/Index"));

// Defer non-critical chrome (mobile gestures, install prompt, swipe nav) so
// framer-motion + supporting code stay out of the initial entry chunk.
const SwipeNavigator = lazy(() => import("@/components/layout/SwipeNavigator").then(m => ({ default: m.SwipeNavigator })));
const MobileFAB = lazy(() => import("@/components/layout/MobileFAB").then(m => ({ default: m.MobileFAB })));
const MobileSwipeHint = lazy(() => import("@/components/layout/MobileSwipeHint").then(m => ({ default: m.MobileSwipeHint })));
const InstallBanner = lazy(() => import("@/components/InstallBanner").then(m => ({ default: m.InstallBanner })));

// Defer Sonner toaster (~10 KB) — toasts only fire after user actions, not at boot.
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
// Defer NotificationBell — pulls realtime subscriptions, not LCP-critical.
const NotificationBell = lazy(() => import("@/components/NotificationBell").then(m => ({ default: m.NotificationBell })));

const PropertySearch = lazy(() => import("./pages/PropertySearch"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const MarketAnalysis = lazy(() => import("./pages/MarketAnalysis"));
const Calculators = lazy(() => import("./pages/Calculators"));
const BRRRRPlanner = lazy(() => import("./pages/BRRRRPlanner"));
const RenovationROI = lazy(() => import("./pages/RenovationROI"));
const HomebuyerJourney = lazy(() => import("./pages/HomebuyerJourney"));
const Programs = lazy(() => import("./pages/Programs"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const DocumentVault = lazy(() => import("./pages/DocumentVault"));
const BuyNowAdvisor = lazy(() => import("./pages/BuyNowAdvisor"));
const Achievements = lazy(() => import("./pages/Achievements"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function useProfile(userId: string | undefined) {
  const [profile, setProfile] = useState<{ display_name: string | null; avatar_url: string | null } | null>(null);
  useEffect(() => {
    if (!userId) { setProfile(null); return; }
    supabase.from("profiles").select("display_name, avatar_url").eq("user_id", userId).maybeSingle()
      .then(({ data }) => setProfile(data));
  }, [userId]);
  return profile;
}

function UserAvatar({ size = "sm" }: { size?: "sm" | "md" }) {
  const { user } = useAuth();
  const profile = useProfile(user?.id);
  const name = profile?.display_name || user?.email || "your account";
  const dim = size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const initials = profile?.display_name
    ? profile.display_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || "?";

  return (
    <Avatar className={dim}>
      {profile?.avatar_url && <AvatarImage src={profile.avatar_url} alt={`Profile photo for ${name}`} />}
      <AvatarFallback className="text-xs bg-primary/10 text-primary">{initials}</AvatarFallback>
    </Avatar>
  );
}

function HeaderAuth() {
  const { user, signOut } = useAuth();
  if (!user) {
    return (
      <Button variant="ghost" size="sm" asChild className="gap-1.5">
        <Link to="/auth"><LogIn className="h-3.5 w-3.5" /> Sign In</Link>
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground hidden sm:inline">{user.email}</span>
      <Link to="/profile">
        <UserAvatar size="sm" />
      </Link>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={signOut} aria-label="Sign out of your account">
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  );
}
function DeferredChrome() {
  // Mounts non-critical providers/widgets after the browser has painted once.
  // Each stage is scheduled in its own requestIdleCallback so individual
  // chunks (radix-toast, sonner, mobile FAB, swipe hint, install banner)
  // download + render in separate tasks instead of one long burst, keeping
  // each task well under the 50ms long-task threshold post-LCP.
  const ready = useAfterPaint();
  // 5 widget stages + 1 trailing stage for route prefetch warm-up.
  const stage = useStagedMount(ready ? 6 : 0, 100);

  useEffect(() => {
    // Run heavy-route prefetch only after every interactive widget has mounted.
    if (stage >= 6) prefetchHeavyRoutesOnIdle();
  }, [stage]);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      {stage > 0 && <Toaster />}
      {stage > 1 && <Sonner />}
      {stage > 2 && <MobileFAB />}
      {stage > 3 && <InstallBanner />}
      {stage > 4 && <MobileSwipeHint />}
    </Suspense>
  );
}

function App() {
  // Defer analytics SDK loading until the user actually interacts with the
  // page (or 10s passes). Zero JS impact on initial paint; events fired
  // before the loader resolves are buffered and flushed automatically.
  useEffect(() => {
    deferAnalytics(async () => {
      const mod = await import("@/lib/analyticsProvider");
      await mod.initAnalyticsProvider();
    });
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <SidebarProvider>
            <div className="min-h-screen flex w-full">
              <div className="hidden md:block">
                <Suspense fallback={null}>
                  <AppSidebar />
                </Suspense>
              </div>
              <div className="flex-1 flex flex-col min-w-0">
                <header className="h-14 flex items-center justify-between border-b border-border/30 px-4 glass-surface shrink-0 sticky top-0 z-10">
                  <div className="flex items-center">
                    <SidebarTrigger className="mr-4 hidden md:flex" />
                    {/* Mobile Logo - shown only on mobile */}
                    <Link to="/" className="flex items-center gap-2 md:hidden">
                      <BrandMark
                        iconClassName="h-7 w-7"
                        textClassName="font-display text-base font-bold tracking-tight text-foreground"
                      />
                    </Link>
                    <span className="hidden md:inline font-display text-sm font-medium text-muted-foreground ml-2">
                      Your Path to Homeownership
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Suspense fallback={null}><NotificationBell /></Suspense>
                    <HeaderAuth />
                    <Suspense fallback={<div className="h-8 w-8" aria-hidden />}>
                      <ThemeToggle />
                    </Suspense>
                  </div>
                </header>
                <RouteMeta />
                <main className="flex-1 overflow-auto pb-14 md:pb-0">
                  <Suspense fallback={null}>
                    <SwipeNavigator>
                      <Routes>
                        <Route path="/" element={<LazyRoute routeLabel="Home" fallback={<DashboardSkeleton />}><PageTransition><Index /></PageTransition></LazyRoute>} />
                        <Route path="/auth" element={<LazyRoute routeLabel="Auth" fallback={<FormPageSkeleton />}><PageTransition><Auth /></PageTransition></LazyRoute>} />
                        <Route path="/reset-password" element={<LazyRoute routeLabel="ResetPassword" fallback={<FormPageSkeleton />}><PageTransition><ResetPassword /></PageTransition></LazyRoute>} />
                        <Route path="/profile" element={<LazyRoute routeLabel="Profile" fallback={<FormPageSkeleton />}><PageTransition><ProfileSettings /></PageTransition></LazyRoute>} />
                        <Route path="/search" element={<LazyRoute routeLabel="PropertySearch" fallback={<PageSkeleton />}><PageTransition><PropertySearch /></PageTransition></LazyRoute>} />
                        <Route path="/property/:id" element={<LazyRoute routeLabel="PropertyDetail" fallback={<PageSkeleton />}><PageTransition><PropertyDetail /></PageTransition></LazyRoute>} />
                        <Route path="/market" element={<LazyRoute routeLabel="MarketAnalysis" fallback={<MarketAnalysisSkeleton />}><PageTransition><MarketAnalysis /></PageTransition></LazyRoute>} />
                        <Route path="/calculators" element={<LazyRoute routeLabel="Calculators" fallback={<CalculatorsSkeleton />}><PageTransition><Calculators /></PageTransition></LazyRoute>} />
                        <Route path="/brrrr" element={<LazyRoute routeLabel="BRRRRPlanner" fallback={<BRRRRSkeleton />}><PageTransition><BRRRRPlanner /></PageTransition></LazyRoute>} />
                        <Route path="/renovations" element={<LazyRoute routeLabel="RenovationROI" fallback={<RenovationSkeleton />}><PageTransition><RenovationROI /></PageTransition></LazyRoute>} />

                        <Route path="/journey" element={<LazyRoute routeLabel="HomebuyerJourney" fallback={<PageSkeleton />}><PageTransition><HomebuyerJourney /></PageTransition></LazyRoute>} />
                        <Route path="/programs" element={<LazyRoute routeLabel="Programs" fallback={<PageSkeleton />}><PageTransition><Programs /></PageTransition></LazyRoute>} />
                        <Route path="/documents" element={<LazyRoute routeLabel="DocumentVault" fallback={<PageSkeleton />}><PageTransition><DocumentVault /></PageTransition></LazyRoute>} />
                        <Route path="/advisor" element={<LazyRoute routeLabel="BuyNowAdvisor" fallback={<FormPageSkeleton />}><PageTransition><BuyNowAdvisor /></PageTransition></LazyRoute>} />
                        <Route path="/achievements" element={<LazyRoute routeLabel="Achievements" fallback={<PageSkeleton />}><PageTransition><Achievements /></PageTransition></LazyRoute>} />
                        <Route path="/.lovable/oauth/consent" element={<LazyRoute routeLabel="OAuthConsent" fallback={<FormPageSkeleton />}><OAuthConsent /></LazyRoute>} />
                        <Route path="*" element={<LazyRoute routeLabel="NotFound" fallback={<PageSkeleton />}><PageTransition><NotFound /></PageTransition></LazyRoute>} />

                      </Routes>
                    </SwipeNavigator>
                  </Suspense>
                </main>
              </div>
              <BottomNav />
              <DeferredChrome />
            </div>
          </SidebarProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
}

export default App;

